import { Component, Inject, OnInit, Optional } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { DocArcService } from '@app/services/doc-arc.service';
import { EdiTypeService } from '@app/services/edi-type.service';
import { HtmlReportService } from '@app/services/html-report.service';
import { LogisticService } from '@app/services/logistic.service';
import { P2ReportDefinitionGet, P2ReportDefinitionService } from '@app/services/p2-report-definition.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';

@Component({
    selector: 'tt-p2-report-def',
    templateUrl: './p2-report-def.component.html',
    styleUrls: ['./p2-report-def.component.css'],
    standalone: false,
})
export class P2ReportDefComponent implements OnInit {
    public page?: P2ReportDefinitionGet;
    public reportDefinitionKeyno = '';
    public selectedTab = 'DETAILS';
    public lockedSave = false;
    public gridPrinters?: GridOptions;
    public webpageGrid?: GridOptions;
    public usergroupGrid?: GridOptions;
    public reportTypes: Awaited<ReturnType<P2ReportDefinitionService['listReportTypes']>> = [];
    public documentCategories: Awaited<ReturnType<DocArcService['getDocumentCategories']>> = [];
    public htmlReports: Awaited<ReturnType<P2ReportDefinitionService['listHtmlReports']>> = [];
    public webPages: Awaited<ReturnType<HtmlReportService['listWebPages']>> = [];
    public ediTypes: Awaited<ReturnType<EdiTypeService['getEdiTypesList']>> = [];
    public printers: Array<{ item_id: string; item_name: string }> = [];
    public paperSizes = [
        { id: 'standard', name: 'papersize_standard' },
        { id: 'custom', name: 'papersize_custom' },
    ];

    public readonly tabs = [
        { id: 'DETAILS', label: 'gb_p2reportdef_details' },
        { id: 'PRINTERS', label: 'gb_p2reportdef_printer' },
        { id: 'WEBPAGES', label: 'gb_p2reportdef_webpages' },
        { id: 'USERGROUPS', label: 'gb_p2reportdef_usergroups' },
    ];

    private navigator?: TTViewNavigator;

    constructor(
        private definitions: P2ReportDefinitionService,
        private docArc: DocArcService,
        private ediTypeService: EdiTypeService,
        private htmlReport: HtmlReportService,
        private logistic: LogisticService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async saveChanges(): Promise<void> {
        if (!this.page || this.lockedSave) return;
        this.lockedSave = true;
        try {
            await this.definitions.saveDefinition(this.page);
            await this.navigator?.back();
        } catch {
            // Keep the form open and unlock saving so the user can retry.
        } finally {
            this.lockedSave = false;
        }
    }

    public async ngOnInit(): Promise<void> {
        this.reportDefinitionKeyno = `${this.navigator?.params['p2_reportdef_keyno'] ?? ''}`;
        if (!this.reportDefinitionKeyno) return;

        const [page, reportTypes, documentCategories, htmlReports, webPages, ediTypes, printerList] = await Promise.all([
            this.definitions.getDefinition({ p2_reportdef_keyno: this.reportDefinitionKeyno }),
            this.definitions.listReportTypes(),
            this.docArc.getDocumentCategories(),
            this.definitions.listHtmlReports(),
            this.htmlReport.listWebPages(),
            this.ediTypeService.getEdiTypesList({}),
            this.logistic.getPrinters(),
        ]);

        page.paper_size_id ??= 'standard';
        page.width ??= 0;
        page.height ??= 0;
        this.page = page;
        this.reportTypes = reportTypes;
        this.documentCategories = documentCategories;
        this.htmlReports = htmlReports;
        this.webPages = webPages;
        this.ediTypes = ediTypes;
        this.printers = [{ item_id: '0', item_name: 'Standard' }, ...printerList.map((printer) => ({ item_id: `${printer.item_id}`, item_name: printer.item_name ?? '' }))];
        this.createGrids();
    }

    private createGrids(): void {
        const keyno = this.reportDefinitionKeyno;
        const kendo = { height: '100%', sortable: true, aggregate: true, pager: false, selectable: false, filterable: false } as const;
        this.gridPrinters = new Grid()
            .setRememberId('w_p2reportdef_grid')
            .setLoadDataFunction({ method: 2536, parameters: { p2_reportdef_keyno: keyno } })
            .setKendoConfig(kendo)
            .setOptionFunc((event: P2ReportPrinterGridEvent) => {
                if (event.func !== 'OnCellClose' || !event.rdata || !this.page) return;
                void this.definitions.setUserReportPrinter(this.page.p2_reportdef_keyno, event.rdata.default_printer_keyno, event.rdata.portal_user_keyno)
                    .then(() => this.gridPrinters?.gridfunc?.rebind());
            });

        this.webpageGrid = new Grid()
            .setRememberId('w_p2reportdef.webpagegrid')
            .setLoadDataFunction({ method: 3410, parameters: { p2_reportdef_keyno: keyno } })
            .setAddRowFunction({ method: null, parameters: { p2_reportdef_keyno: keyno }, autoSave: true })
            .setSaveDataFunction({ method: null, parameters: { p2_reportdef_keyno: keyno }, autoSave: true })
            .setRemoveRowFunction({ method: null, parameters: {}, autoSave: true })
            .setKendoConfig(kendo)
            .setSpecialFunc({ buttons: [{
                name: 'customDelete', text: '', icon: 'far-trash-alt',
                func: async (row) => {
                    await this.webpageGrid?.gridfunc?.removeRow(row);
                    await this.webpageGrid?.gridfunc?.saveChanges(false);
                },
            }] });

        this.usergroupGrid = new Grid()
            .setRememberId('w_p2reportdef.usergroupGrid')
            .setLoadDataFunction({ method: 3481, parameters: { p2_reportdef_keyno: keyno } })
            .setKendoConfig(kendo);
    }
}

interface P2ReportPrinterGridEvent {
    func?: string;
    rdata?: { default_printer_keyno: string; portal_user_keyno: string };
}
